import React, { useEffect, useRef } from 'react';
import { scaleLinear } from 'd3-scale';
import { extentLinear } from '@d3fc/d3fc-extent';
import { chartCartesian } from '@d3fc/d3fc-chart';
import { VolumeCapsuleData } from 'widgets/liquiditySummary/volumeCapsule/components/volumeCapsuleData';
import { volumeCapsuleChartSeries } from 'widgets/liquiditySummary/volumeCapsule/components/chart/volumeCapsuleChartSeries';
import { LiquiditySummaryChartSeriesPoint } from 'widgets/liquiditySummary/chart/liquiditySummaryChartData';
import { select } from 'd3-selection';
import { renderXAxis } from './xAxis';

import './VolumeCapsuleChart.scss';
import { VolumeCapsuleChartAdvLabel } from 'widgets/liquiditySummary/volumeCapsule/components/chart/VolumeCapsuleChartAdvLabel';
import { useVolumeCapsuleConfigSlice } from 'widgets/liquiditySummary/volumeCapsule/state/config/configslice';

export interface VolumeCapsuleChartProps {
    chartData: VolumeCapsuleData;
}

export function VolumeCapsuleChart(props: VolumeCapsuleChartProps) {
    const { chartData } = props;
    const {
        config: { showQuantitiesOnScale },
    } = useVolumeCapsuleConfigSlice();

    const nodeRef = useRef<HTMLDivElement>(null);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const chartRef = useRef<any>();

    const xScaleRef = useRef(scaleLinear());
    const yScaleRef = useRef(scaleLinear());

    const xExtent = extentLinear()
        .accessors([
            (a: LiquiditySummaryChartSeriesPoint[]) => {
                return a.map((d: LiquiditySummaryChartSeriesPoint) => {
                    return d[1];
                });
            },
        ])
        .pad([0, 0.1]);

    useEffect(() => {
        chartRef.current = chartCartesian(xScaleRef.current, yScaleRef.current)
            .yOrient('left')
            .xTickSizeOuter(0);
    }, []);

    useEffect(() => {
        const node = nodeRef.current!;
        const chart = chartRef.current!;

        const chartSeries = volumeCapsuleChartSeries(chartData);

        xScaleRef.current.domain(xExtent(chartData.stackedSeriesData));
        yScaleRef.current.domain([-1, 2]);

        chart.svgPlotArea(chartSeries);
        select(node).datum(chartData.stackedSeriesData).call(chart);

        renderXAxis(chartRef.current, chartData, showQuantitiesOnScale);
    }, [chartData, xExtent, showQuantitiesOnScale]);

    if (chartData.todayTotal === 0 && chartData.historicalTotal === 0) {
        return <></>;
    }

    return (
        <div className="VolumeCapsuleChart flippable" ref={nodeRef}>
            <VolumeCapsuleChartAdvLabel data={chartData} />
        </div>
    );
}
